/**
 * Discount math — shared client/server (Wave 1).
 *
 * Replaces the hand-mirrored pair: `sanitizeDiscountPercent` + the per-item
 * net math lived in BOTH src/services/quote-pricing.ts (client: itemTotal)
 * and functions/src/invoices/quote-discount.ts (server: resolveItemDiscount).
 * The customer signs the discounted proposal total, and the invoice must
 * charge the same pennies — one implementation makes drift impossible.
 */
import { roundMoney, toFiniteMoney } from './round';

/**
 * Coerce an untrusted discount % into a valid number in [0, 100].
 * Rules (identical on both sides since inception):
 *   - non-numeric / null / undefined / ≤ 0 → 0 (no discount)
 *   - numeric strings parse
 *   - clamped to a 100% ceiling (never invert a total)
 */
export function sanitizeDiscountPercent(pct: unknown): number {
  const n = toFiniteMoney(pct);
  if (n === null || n <= 0) return 0;
  return n >= 100 ? 100 : n;
}

export interface NetLineTotal {
  /** Sanitized per-item percent (0 = none). */
  percent: number;
  /** LIST line total (unitPrice × quantity), ex-GST, rounded. */
  listExGst: number;
  /** NET line total after the per-item discount, ex-GST, rounded. */
  netExGst: number;
  /** Discount $ (list − net), ex-GST, ≥ 0. */
  amountExGst: number;
}

/**
 * The penny-identical per-line net math both sides must share:
 *   list = round(unitPrice × quantity)
 *   net  = percent > 0 ? round(list × (1 − percent/100)) : list
 * (Two-step rounding is deliberate and load-bearing — the customer sees the
 * rounded list figure struck through, so the discount applies to IT.)
 */
export function netLineTotal(
  unitPrice: number,
  quantity: number,
  discountPercentRaw: unknown,
): NetLineTotal {
  const percent = sanitizeDiscountPercent(discountPercentRaw);
  const listExGst = roundMoney(
    (Number.isFinite(unitPrice) ? unitPrice : 0) * (Number.isFinite(quantity) ? quantity : 0),
  );
  if (percent <= 0) {
    return { percent: 0, listExGst, netExGst: listExGst, amountExGst: 0 };
  }
  const netExGst = roundMoney(listExGst * (1 - percent / 100));
  return { percent, listExGst, netExGst, amountExGst: roundMoney(listExGst - netExGst) };
}
